import React, { createContext, useState, useContext, useEffect } from 'react';
import { STORAGE_KEYS } from '../config/config';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (credentials, loginType = 'admin') => {
    try {
      const apiCall = loginType === 'staff' ? authAPI.staffLogin(credentials) : authAPI.login(credentials);
      const response = await apiCall;
      if (response.data.success) {
        const userData = response.data.data;
        setUser(userData);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        return { success: true, data: userData };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (data) => {
    try {
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...registrationData } = data;
      const response = await authAPI.register(registrationData);
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      const errors = error.response?.data?.errors || {};
      
      // Show first validation error if available
      if (Object.keys(errors).length > 0) {
        const firstError = Object.values(errors)[0][0];
        return { success: false, message: firstError };
      }
      
      return { 
        success: false, 
        message: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
  };

  // Helper methods to check institute type
  const isOnlineInstitute = () => {
    return !user?.institute_type || user?.institute_type === 'online';
  };

  const isOfflineInstitute = () => {
    return user?.institute_type === 'offline';
  };

  const isLibrary = () => {
    return user?.institute_type === 'library';
  };

  const isBothInstitute = () => {
    return user?.institute_type === 'both';
  };

  const hasFeature = (feature) => {
    const type = user?.institute_type || 'online';
    
    // Feature availability matrix
    const features = {
      courses: ['online', 'both'],
      classes: ['offline', 'both'],
      timetable: ['offline', 'both'],
      attendance: ['offline', 'both'],
      assignments: ['offline', 'both'],
      library: ['library', 'both'],
      payments: ['online', 'offline', 'both'],
      students: ['online', 'offline', 'library', 'both']
    };
    
    return features[feature]?.includes(type) ?? true;
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    isOnlineInstitute,
    isOfflineInstitute,
    isLibrary,
    isBothInstitute,
    hasFeature
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

